import React, { useCallback, useEffect, useState } from "react";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PublishIcon from "@mui/icons-material/Publish";
import SearchIcon from "@mui/icons-material/Search";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import UtilService from "../../services/UtilService";
import AdminStoryDetail from "./AdminStoryDetail";
import AdminStoryService, { clampStoryBatchSize } from "./AdminStoryService";
import Logger from "../../services/Logger";
import {
    getStoryCardImageUrl,
    getStoryFullImageUrl,
    getStoryEnglishTitle,
    getStoryEnglishText,
    getStoryFacebookPublishedAt,
    getStoryHighPriorityPublish,
    getStoryInstagramPublishedAt,
    getStoryTplPublishedAt,
} from "./storyDisplayUtils";
import StoryImageZoomDialog from "./StoryImageZoomDialog";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;
const HIDE_FRENCH_KEY = "admin.hideFrench";
const STORY_LIST_SORT_KEY = "admin.story.list.sort";
const STORY_LIST_LANGUAGE_KEY = "admin.story.list.language";
const STORY_LIST_SEARCH_KEY = "admin.story.list.search";
const STORY_LIST_STANDALONE_KEY = "admin.story.list.standaloneFilter";
const STORY_LIST_STANDALONE_LEGACY_KEY = "admin.story.list.standaloneImageOnly";
const STORY_LIST_POSTED_KEY = "admin.story.list.posted";
const STORY_LIST_PAGE_KEY = "admin.story.list.page";
const STORY_LIST_PAGE_SIZE_KEY = "admin.story.list.pageSize";

function readHideFrenchPreference() {
    try {
        const stored = localStorage.getItem(HIDE_FRENCH_KEY);
        if (stored === null) return true;
        return stored === "true";
    } catch {
        return true;
    }
}

const ORDER_BY_OPTIONS = [
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "updatedAt:desc", label: "Updated at (newest)" },
    { value: "updatedAt:asc", label: "Updated at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];

/** Filters GET /v1/story when set; empty omits `language` query param. */
const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

const STANDALONE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "true", label: "Standalone" },
    { value: "false", label: "Not standalone" },
];

const POSTED_FILTER_OPTIONS = [
    { value: "false", label: "Not posted" },
    { value: "instagram", label: "Instagram only" },
    { value: "facebook", label: "Facebook only" },
    { value: "both", label: "Both" },
];

const VALID_POSTED_VALUES = new Set(POSTED_FILTER_OPTIONS.map((o) => o.value));

const VALID_SORT_VALUES = new Set(ORDER_BY_OPTIONS.map((o) => o.value));
const VALID_LANGUAGE_VALUES = new Set(LANGUAGE_FILTER_OPTIONS.map((o) => o.value));

function readStoredStorySort() {
    try {
        const stored = localStorage.getItem(STORY_LIST_SORT_KEY);
        if (stored && VALID_SORT_VALUES.has(stored)) return stored;
    } catch {
        /* ignore */
    }
    return "publishedAt:desc";
}

function readStoredStoryLanguage() {
    try {
        const stored = localStorage.getItem(STORY_LIST_LANGUAGE_KEY);
        if (stored != null && VALID_LANGUAGE_VALUES.has(stored)) return stored;
    } catch {
        /* ignore */
    }
    return "";
}

function readStoredStorySearch() {
    try {
        const stored = localStorage.getItem(STORY_LIST_SEARCH_KEY);
        return typeof stored === "string" ? stored : "";
    } catch {
        return "";
    }
}

function readStoredStoryStandaloneFilter() {
    try {
        const stored = localStorage.getItem(STORY_LIST_STANDALONE_KEY);
        if (stored === "" || stored === "true" || stored === "false") return stored;
        if (localStorage.getItem(STORY_LIST_STANDALONE_LEGACY_KEY) === "true") return "true";
    } catch {
        /* ignore */
    }
    return "";
}

/** Default Not posted. Older checkbox true meant both channels. */
function readStoredStoryPosted() {
    try {
        const stored = localStorage.getItem(STORY_LIST_POSTED_KEY);
        if (stored === "true") return "both";
        if (stored && VALID_POSTED_VALUES.has(stored)) return stored;
    } catch {
        /* ignore */
    }
    return "false";
}

function readStoredStoryPage() {
    try {
        const n = parseInt(localStorage.getItem(STORY_LIST_PAGE_KEY), 10);
        if (Number.isFinite(n) && n >= 1) return n;
    } catch {
        /* ignore */
    }
    return 1;
}

function readStoredStoryPageSize() {
    try {
        const n = parseInt(localStorage.getItem(STORY_LIST_PAGE_SIZE_KEY), 10);
        if (PAGE_SIZE_OPTIONS.includes(n)) return n;
    } catch {
        /* ignore */
    }
    return DEFAULT_PAGE_SIZE;
}

function writeStoryListPrefs({ sort, language, searchQuery, standaloneFilter, posted, page, pageSize }) {
    try {
        if (sort && VALID_SORT_VALUES.has(sort)) {
            localStorage.setItem(STORY_LIST_SORT_KEY, sort);
        }
        if (language != null && VALID_LANGUAGE_VALUES.has(language)) {
            localStorage.setItem(STORY_LIST_LANGUAGE_KEY, language);
        }
        if (searchQuery != null) {
            localStorage.setItem(STORY_LIST_SEARCH_KEY, searchQuery);
        }
        if (standaloneFilter === "" || standaloneFilter === "true" || standaloneFilter === "false") {
            localStorage.setItem(STORY_LIST_STANDALONE_KEY, standaloneFilter);
        }
        if (posted != null && VALID_POSTED_VALUES.has(posted)) {
            localStorage.setItem(STORY_LIST_POSTED_KEY, posted);
        }
        if (page != null) {
            const n = Math.floor(Number(page));
            if (Number.isFinite(n) && n >= 1) {
                localStorage.setItem(STORY_LIST_PAGE_KEY, String(n));
            }
        }
        if (pageSize != null && PAGE_SIZE_OPTIONS.includes(pageSize)) {
            localStorage.setItem(STORY_LIST_PAGE_SIZE_KEY, String(pageSize));
        }
    } catch {
        /* ignore quota / private mode */
    }
}

const STATS_BUCKETS = [
    { key: "total", label: "Total" },
    { key: "english", label: "English" },
    { key: "french", label: "French" },
];

const STATS_BASE_FIELDS = [
    { key: "reviewedCount", label: "Reviewed" },
    { key: "publishedCount", label: "Published" },
    { key: "withStoryImage", label: "Story image", expectFullPublished: true },
    { key: "withPreviewImage", label: "Preview", expectFullPublished: true },
    { key: "withIcon400", label: "Icon 400", expectFullPublished: true },
    { key: "withIcon200", label: "Icon 200", expectFullPublished: true },
    { key: "withDisplayClue", label: "With display clue", expectFullPublished: true },
];

/** Sliding / Jigsaw: shared sub-column labels under a group header row. */
const STATS_PUZZLE_SUBCOLS = [
    { key: "stories", label: "Total" },
    { key: "preview", label: "Preview" },
    { key: "image", label: "Full image" },
];

const STATS_PUZZLE_GROUPS = [
    {
        id: "sliding",
        label: "Sliding",
        columns: [
            { key: "withSlidingStories", role: "total" },
            { key: "withSlidingPreviewComplete", role: "preview" },
            { key: "withSlidingImageComplete", role: "image" },
        ],
    },
    {
        id: "jigsaw",
        label: "Jigsaw",
        columns: [
            { key: "withJigsawStories", role: "total" },
            { key: "withJigsawPreviewComplete", role: "preview" },
            { key: "withJigsawImageComplete", role: "image" },
        ],
    },
    {
        id: "standalone",
        label: "Standalone Image",
        columns: [
            { key: "withStandaloneStories", role: "total" },
            { key: "withStandalonePreviewComplete", role: "preview" },
            { key: "withStandaloneImageComplete", role: "image" },
        ],
    },
];

function getPuzzleGroupTotalKey(fieldKey) {
    for (const g of STATS_PUZZLE_GROUPS) {
        const col = g.columns.find((c) => c.key === fieldKey);
        if (!col || col.role === "total") continue;
        return g.columns.find((c) => c.role === "total")?.key ?? null;
    }
    return null;
}

const STATS_ALL_FIELDS = [
    ...STATS_BASE_FIELDS,
    ...STATS_PUZZLE_GROUPS.flatMap((g) => g.columns),
];

const headerCellSx = { fontWeight: 700, whiteSpace: "nowrap", fontSize: "0.8rem" };
const groupHeaderCellSx = {
    ...headerCellSx,
    textAlign: "center",
    borderBottom: 1,
    borderColor: "divider",
    bgcolor: "action.hover",
};

function renderStatsValueCell(row, field) {
    const { key: fk, expectFullPublished, role } = field;
    const v = row?.[fk];
    const cellFinite = typeof v === "number" && Number.isFinite(v);
    const display = cellFinite ? v : "—";
    const publishedVal = row?.publishedCount;
    const pubFinite = typeof publishedVal === "number" && Number.isFinite(publishedVal);
    let isIssue =
        expectFullPublished &&
        fk !== "publishedCount" &&
        fk !== "reviewedCount" &&
        pubFinite &&
        (!cellFinite || v !== publishedVal);
    if ((role === "preview" || role === "image") && !isIssue) {
        const totalKey = getPuzzleGroupTotalKey(fk);
        const totalVal = totalKey != null ? row?.[totalKey] : null;
        const totalFinite = typeof totalVal === "number" && Number.isFinite(totalVal);
        if (totalFinite && (!cellFinite || v < totalVal)) {
            isIssue = true;
        }
    }
    return (
        <TableCell
            key={fk}
            align="right"
            sx={isIssue ? { color: "error.main", fontWeight: 700 } : undefined}
        >
            {display}
        </TableCell>
    );
}

function StoryStatsTableHead({ loading }) {
    if (loading) {
        return (
            <>
                <TableRow>
                    <TableCell rowSpan={2} sx={{ width: 88 }}>
                        <Skeleton variant="text" width={48} />
                    </TableCell>
                    {STATS_BASE_FIELDS.map((f) => (
                        <TableCell key={f.key} rowSpan={2} align="right">
                            <Skeleton variant="text" width={72} sx={{ ml: "auto" }} />
                        </TableCell>
                    ))}
                    {STATS_PUZZLE_GROUPS.map((g) => (
                        <TableCell key={g.id} colSpan={STATS_PUZZLE_SUBCOLS.length} align="center">
                            <Skeleton variant="text" width={56} sx={{ mx: "auto" }} />
                        </TableCell>
                    ))}
                </TableRow>
                <TableRow>
                    {STATS_PUZZLE_GROUPS.flatMap((g) =>
                        STATS_PUZZLE_SUBCOLS.map((sub) => (
                            <TableCell key={`${g.id}-${sub.key}-sk`} align="right">
                                <Skeleton variant="text" width={48} sx={{ ml: "auto" }} />
                            </TableCell>
                        ))
                    )}
                </TableRow>
            </>
        );
    }
    return (
        <>
            <TableRow>
                <TableCell rowSpan={2} sx={headerCellSx} />
                {STATS_BASE_FIELDS.map(({ key: fk, label: fl }) => (
                    <TableCell key={fk} rowSpan={2} align="right" sx={headerCellSx}>
                        {fl}
                    </TableCell>
                ))}
                {STATS_PUZZLE_GROUPS.map((g) => (
                    <TableCell
                        key={g.id}
                        colSpan={STATS_PUZZLE_SUBCOLS.length}
                        align="center"
                        sx={groupHeaderCellSx}
                    >
                        {g.label}
                    </TableCell>
                ))}
            </TableRow>
            <TableRow>
                {STATS_PUZZLE_GROUPS.flatMap((g) =>
                    STATS_PUZZLE_SUBCOLS.map((sub) => (
                        <TableCell
                            key={`${g.id}-${sub.key}`}
                            align="right"
                            sx={{ ...headerCellSx, color: "text.secondary" }}
                        >
                            {sub.label}
                        </TableCell>
                    ))
                )}
            </TableRow>
        </>
    );
}

export default function AdminStoryMain() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(readStoredStoryPage);
    const [pageSize, setPageSize] = useState(readStoredStoryPageSize);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState(readStoredStorySort);
    const [language, setLanguage] = useState(readStoredStoryLanguage);
    const [searchQuery, setSearchQuery] = useState(readStoredStorySearch);
    const [standaloneFilter, setStandaloneFilter] = useState(readStoredStoryStandaloneFilter);
    const [posted, setPosted] = useState(readStoredStoryPosted);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const [batchDialog, setBatchDialog] = useState(null);
    const [batchSizeInput, setBatchSizeInput] = useState("10");
    const [batchSubmitting, setBatchSubmitting] = useState(false);

    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState(null);
    const [hideFrench, setHideFrench] = useState(readHideFrenchPreference);
    const [savingHighPriorityStoryId, setSavingHighPriorityStoryId] = useState(null);
    const [detailStoryId, setDetailStoryId] = useState(null);
    const [storyTextDialog, setStoryTextDialog] = useState(null);
    const [fullImageStory, setFullImageStory] = useState(null);
    const [deleteDialogStory, setDeleteDialogStory] = useState(null);
    const [deletingStoryId, setDeletingStoryId] = useState(null);
    const [regenDialogStory, setRegenDialogStory] = useState(null);
    const [regenImageLoadingId, setRegenImageLoadingId] = useState(null);
    const [publishSocialDialogStory, setPublishSocialDialogStory] = useState(null);
    const [publishingSocialStoryId, setPublishingSocialStoryId] = useState(null);

    const visibleStatsBuckets = hideFrench
        ? STATS_BUCKETS.filter((b) => b.key !== "french")
        : STATS_BUCKETS;

    useEffect(() => {
        writeStoryListPrefs({ sort, language, searchQuery, standaloneFilter, posted, page, pageSize });
    }, [sort, language, searchQuery, standaloneFilter, posted, page, pageSize]);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        setStatsError(null);
        const result = await AdminStoryService.getStats();
        setStatsLoading(false);
        if (result.success && result.data && typeof result.data === "object") {
            setStats(result.data);
        } else {
            setStats(null);
            setStatsError(result.error || "Failed to load stats");
        }
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        const pageIndex = Math.max(0, page - 1);
        const result = await AdminStoryService.list(
            pageIndex,
            pageSize,
            sort,
            language,
            searchQuery,
            standaloneFilter === "true" ? true : standaloneFilter === "false" ? false : undefined,
            posted
        );
        setLoading(false);
        if (result.success && result.data) {
            const nextTotal = result.data.totalElements;
            setItems(result.data.content);
            setTotalElements(nextTotal);
            const nextPageCount = Math.max(1, Math.ceil((nextTotal || 0) / pageSize) || 1);
            if (page > nextPageCount) {
                setPage(nextPageCount);
            }
        } else {
            setItems([]);
            setTotalElements(0);
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to load stories",
            });
        }
    }, [page, pageSize, sort, language, searchQuery, standaloneFilter, posted]);

    const handlePageSizeChange = (e) => {
        const next = Number(e.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        setPageSize(next);
        setPage(1);
    };

    const closeStoryDetail = useCallback(() => {
        setDetailStoryId(null);
        fetchList();
        fetchStats();
    }, [fetchList, fetchStats]);

    const openStoryText = useCallback(async (story) => {
        const sid = story?.id;
        if (!sid) return;
        const title = getStoryEnglishTitle(story);
        const cached = getStoryEnglishText(story);
        setStoryTextDialog({ id: sid, title, text: cached, loading: true, error: "" });
        const result = await AdminStoryService.getById(sid);
        setStoryTextDialog((prev) => {
            if (!prev || prev.id !== sid) return prev;
            if (!result.success) {
                return {
                    ...prev,
                    loading: false,
                    text: cached,
                    error: cached ? "" : (result.error || "Could not load the story."),
                };
            }
            const text = getStoryEnglishText(result.data) || cached;
            return { ...prev, loading: false, text, error: "" };
        });
    }, []);

    const handleConfirmRegenImage = useCallback(async (storyId) => {
        if (!storyId) return;
        setRegenImageLoadingId(storyId);
        try {
            const result = await AdminStoryService.regenImage(storyId);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Regenerate image failed",
                });
                return;
            }
            if (result.data && typeof result.data === "object") {
                setItems((prev) =>
                    prev.map((story) => (story.id === storyId ? { ...story, ...result.data } : story)),
                );
            } else {
                fetchList();
            }
            setSnackbar({
                open: true,
                severity: "success",
                message: "Image replaced. Story text was not changed.",
            });
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Regenerate image failed",
            });
        } finally {
            setRegenImageLoadingId(null);
        }
    }, [fetchList]);

    const handleConfirmDeleteStory = useCallback(async (storyId) => {
        if (!storyId) return;
        setDeletingStoryId(storyId);
        Logger.info("[STORY_DELETE] confirm delete storyId=", storyId);
        try {
            const result = await AdminStoryService.deleteById(storyId);
            Logger.info("[STORY_DELETE] result", result);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Delete failed",
                });
                return;
            }
            setFullImageStory(null);
            setDeleteDialogStory(null);
            setItems((prev) => prev.filter((story) => story.id !== storyId));
            setTotalElements((n) => Math.max(0, (Number(n) || 0) - 1));
            fetchStats();
            setSnackbar({
                open: true,
                severity: "success",
                message: "Story deleted",
            });
        } catch (err) {
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Delete failed",
            });
        } finally {
            setDeletingStoryId(null);
        }
    }, [fetchStats]);

    const handleHighPriorityChange = useCallback(async (storyId, highPriorityPublish) => {
        if (!storyId) return;
        setSavingHighPriorityStoryId(storyId);
        const result = await AdminStoryService.setHighPriorityPublish(storyId, highPriorityPublish);
        setSavingHighPriorityStoryId(null);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to prioritize Facebook and Instagram",
            });
            return;
        }
        setItems((prev) =>
            prev.map((story) =>
                story.id === storyId ? { ...story, highPriorityPublish } : story,
            ),
        );
        setSnackbar({
            open: true,
            severity: "success",
            message: highPriorityPublish
                ? "Facebook and Instagram prioritized"
                : "Facebook and Instagram priority cleared",
        });
    }, []);

    const handleConfirmPublishSocial = useCallback(async (storyId) => {
        if (!storyId || publishingSocialStoryId) return;
        setPublishSocialDialogStory(null);
        setPublishingSocialStoryId(storyId);
        const result = await AdminStoryService.publishSocial(storyId);
        setPublishingSocialStoryId(null);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Social posting failed",
            });
            return;
        }
        const data = result.data || {};
        const fbOk = data.facebook?.published === true;
        const igOk = data.instagram?.published === true;
        const updated = data.story && typeof data.story === "object" ? data.story : null;
        if (updated) {
            setItems((prev) =>
                prev.map((story) => (story.id === storyId ? { ...story, ...updated } : story)),
            );
        } else if (fbOk || igOk) {
            const now = Date.now();
            setItems((prev) =>
                prev.map((story) =>
                    story.id === storyId
                        ? {
                              ...story,
                              ...(fbOk ? { facebookPublishedAt: story.facebookPublishedAt ?? now } : {}),
                              ...(igOk ? { instagramPublishedAt: story.instagramPublishedAt ?? now } : {}),
                          }
                        : story,
                ),
            );
        }
        const parts = [];
        if (fbOk) parts.push("Facebook");
        else if (data.facebook?.reason) parts.push(`Facebook skipped (${data.facebook.reason})`);
        if (igOk) parts.push("Instagram");
        else if (data.instagram?.reason) parts.push(`Instagram skipped (${data.instagram.reason})`);
        setSnackbar({
            open: true,
            severity: fbOk && igOk ? "success" : "warning",
            message:
                fbOk && igOk
                    ? "Posted to Facebook and Instagram"
                    : parts.length
                      ? parts.join(". ")
                      : "Social posting finished with warnings",
        });
    }, [publishingSocialStoryId]);

    const closeBatchDialog = () => {
        if (!batchSubmitting) setBatchDialog(null);
    };

    const runBatch = async () => {
        if (!batchDialog) return;
        const size = batchDialog === "disable"
            ? clampStoryBatchSize(batchSizeInput, 5000)
            : clampStoryBatchSize(batchSizeInput);
        setBatchSubmitting(true);
        const result =
            batchDialog === "story"
                ? await AdminStoryService.genStoryBatch(size)
                : batchDialog === "disable"
                    ? await AdminStoryService.batchDisableContent(size)
                    : await AdminStoryService.genImageBatch(size);
        setBatchSubmitting(false);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Batch request failed",
            });
            return;
        }
        const data = result.data || {};
        const processed = typeof data.processed === "number" ? data.processed : 0;
        const errors = Array.isArray(data.errors) ? data.errors : [];
        const errCount = errors.length;
        let msg = `Processed ${processed} item${processed === 1 ? "" : "s"}.`;
        const remaining = typeof data.remaining === "number" ? data.remaining : 0;
        if (remaining > 0) {
            msg += ` ${remaining} still remaining — run again.`;
        }
        if (errCount > 0) {
            msg += ` ${errCount} error${errCount === 1 ? "" : "s"}.`;
            if (errors[0]) msg += ` ${String(errors[0]).slice(0, 160)}`;
        }
        setSnackbar({
            open: true,
            severity: errCount ? "warning" : "success",
            message: msg,
        });
        setBatchDialog(null);
        fetchList();
        fetchStats();
    };

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const pageCount = Math.max(1, Math.ceil(totalElements / pageSize) || 1);

    return (
        <Box sx={{ flexGrow: 1, p: 2, width: "100%", maxWidth: "none", boxSizing: "border-box" }}>
            <Accordion
                defaultExpanded
                sx={{
                    mb: 2,
                    borderRadius: "8px !important",
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden />}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            pr: 1,
                            gap: 2,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Story Statistics
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={hideFrench}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const next = e.target.checked;
                                        setHideFrench(next);
                                        try {
                                            localStorage.setItem(HIDE_FRENCH_KEY, String(next));
                                        } catch {
                                            /* ignore */
                                        }
                                    }}
                                />
                            }
                            label="Hide French"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ mr: 0 }}
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    {statsLoading ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ width: "100%" }}>
                            <Table size="small" sx={{ width: "100%", minWidth: 1100 }}>
                                <TableHead>
                                    <StoryStatsTableHead loading />
                                </TableHead>
                                <TableBody>
                                    {visibleStatsBuckets.map((b) => (
                                        <TableRow key={b.key}>
                                            <TableCell>
                                                <Skeleton variant="text" width={56} />
                                            </TableCell>
                                            {STATS_ALL_FIELDS.map((f) => (
                                                <TableCell key={f.key} align="right">
                                                    <Skeleton variant="text" width={40} sx={{ ml: "auto" }} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : statsError ? (
                        <Typography variant="body2" color="error">
                            {statsError}
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ width: "100%", overflowX: "auto" }}>
                            <Table size="small" sx={{ width: "100%", minWidth: 1100 }}>
                                <TableHead>
                                    <StoryStatsTableHead loading={false} />
                                </TableHead>
                                <TableBody>
                                    {visibleStatsBuckets.map(({ key: bk, label: bl }) => {
                                        const bucket = stats?.[bk];
                                        const row =
                                            bucket && typeof bucket === "object" ? bucket : null;
                                        return (
                                            <TableRow key={bk} hover>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {bl}
                                                </TableCell>
                                                {STATS_ALL_FIELDS.map((field) =>
                                                    renderStatsValueCell(row, field)
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            <Box sx={{ mb: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AutoStoriesOutlinedIcon color="primary" />
                        Stories
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setBatchSizeInput("10");
                                setBatchDialog("story");
                            }}
                        >
                            Batch Story
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setBatchSizeInput("10");
                                setBatchDialog("preview");
                            }}
                        >
                            Batch Preview
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => {
                                setBatchSizeInput("5000");
                                setBatchDialog("disable");
                            }}
                        >
                            Batch disabled content
                        </Button>
                    </Stack>
                </Box>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    flexWrap="wrap"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ mt: 2 }}
                >
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
                        <InputLabel id="story-language-filter-label">Language</InputLabel>
                        <Select
                            labelId="story-language-filter-label"
                            label="Language"
                            value={language}
                            onChange={(e) => {
                                setLanguage(e.target.value);
                                setPage(1);
                            }}
                        >
                            {LANGUAGE_FILTER_OPTIONS.map((o) => (
                                <MenuItem key={o.value || "all"} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                        <InputLabel id="story-order-by-label">Order by</InputLabel>
                        <Select
                            labelId="story-order-by-label"
                            label="Order by"
                            value={sort}
                            onChange={(e) => {
                                setSort(e.target.value);
                                setPage(1);
                            }}
                        >
                            {ORDER_BY_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                        <InputLabel id="story-standalone-filter-label">Standalone image</InputLabel>
                        <Select
                            labelId="story-standalone-filter-label"
                            label="Standalone image"
                            value={standaloneFilter}
                            onChange={(e) => {
                                setStandaloneFilter(e.target.value);
                                setPage(1);
                            }}
                        >
                            {STANDALONE_FILTER_OPTIONS.map((o) => (
                                <MenuItem key={o.value || "all"} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                        <InputLabel id="story-posted-filter-label">Posted</InputLabel>
                        <Select
                            labelId="story-posted-filter-label"
                            label="Posted"
                            value={posted}
                            onChange={(e) => {
                                setPosted(e.target.value);
                                setPage(1);
                            }}
                        >
                            {POSTED_FILTER_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        placeholder="Search by name..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                        <IconButton
                                            type="button"
                                            size="small"
                                            edge="end"
                                            aria-label="Clear search"
                                            onClick={() => {
                                                setSearchQuery("");
                                                setPage(1);
                                            }}
                                            sx={{ width: 44, height: 44 }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            },
                        }}
                        sx={{ width: { xs: "100%", sm: 180 }, maxWidth: 180 }}
                    />
                </Stack>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {totalElements} stor{totalElements === 1 ? "y" : "ies"}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    width: "100%",
                    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 240px), 1fr))",
                    gap: 2,
                    mb: 0,
                }}
            >
                {loading
                    ? Array.from({ length: pageSize }).map((_, i) => (
                          <Card key={`sk-${i}`} variant="outlined" sx={{ borderRadius: 2 }}>
                              <Skeleton
                                  variant="rectangular"
                                  animation="wave"
                                  sx={{ width: "100%", aspectRatio: "1 / 1" }}
                              />
                              <CardContent>
                                  <Skeleton width="90%" animation="wave" />
                                  <Skeleton width="55%" animation="wave" />
                              </CardContent>
                          </Card>
                      ))
                    : items.map((story) => {
                          const sid = story.id;
                          const title = getStoryEnglishTitle(story);
                          const imgUrl = getStoryCardImageUrl(story);
                          const fullUrl = getStoryFullImageUrl(story);
                          const tplPublishedAt = getStoryTplPublishedAt(story);
                          const facebookPublishedAt = getStoryFacebookPublishedAt(story);
                          const instagramPublishedAt = getStoryInstagramPublishedAt(story);
                          const tplPublishedLabel = tplPublishedAt
                              ? UtilService.formatDate(tplPublishedAt)
                              : null;
                          const facebookPublishedLabel = facebookPublishedAt
                              ? UtilService.formatDate(facebookPublishedAt)
                              : null;
                          const instagramPublishedLabel = instagramPublishedAt
                              ? UtilService.formatDate(instagramPublishedAt)
                              : null;
                          const highPriority = getStoryHighPriorityPublish(story);
                          const savingHighPriority = Boolean(sid && savingHighPriorityStoryId === sid);
                          const socialStillMissing =
                              !facebookPublishedAt || !instagramPublishedAt;
                          const publishingSocial = Boolean(sid && publishingSocialStoryId === sid);
                          return (
                              <Card
                                  key={sid || title}
                                  variant="outlined"
                                  sx={{
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      cursor: sid ? "pointer" : "default",
                                      transition: "box-shadow 0.2s, transform 0.15s",
                                      "&:hover": sid
                                          ? {
                                                boxShadow: 4,
                                                transform: "translateY(-2px)",
                                            }
                                          : {},
                                      display: "flex",
                                      flexDirection: "column",
                                      height: "100%",
                                  }}
                                  onClick={() => sid && setDetailStoryId(sid)}
                              >
                                  <Box
                                      sx={{
                                          position: "relative",
                                          width: "100%",
                                          aspectRatio: "1 / 1",
                                          bgcolor: "action.hover",
                                          overflow: "hidden",
                                      }}
                                  >
                                      <Tooltip title="Prioritize Facebook and Instagram">
                                          <Checkbox
                                              size="small"
                                              checked={highPriority}
                                              disabled={!sid || savingHighPriority}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={(e) => {
                                                  e.stopPropagation();
                                                  if (!sid || savingHighPriority) return;
                                                  handleHighPriorityChange(sid, e.target.checked);
                                              }}
                                              sx={{
                                                  position: "absolute",
                                                  top: 6,
                                                  left: 6,
                                                  zIndex: 2,
                                                  p: 0.25,
                                                  bgcolor: "background.paper",
                                                  borderRadius: 0.5,
                                                  boxShadow: 1,
                                              }}
                                              inputProps={{ "aria-label": "Prioritize Facebook and Instagram" }}
                                          />
                                      </Tooltip>
                                      <Tooltip title="Open full image">
                                          <span>
                                              <IconButton
                                                  size="small"
                                                  aria-label="Open full image"
                                                  disabled={!fullUrl}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (!fullUrl) return;
                                                      setFullImageStory({ id: sid, url: fullUrl, title });
                                                  }}
                                                  sx={{
                                                      position: "absolute",
                                                      top: 6,
                                                      right: 6,
                                                      zIndex: 2,
                                                      p: 0.25,
                                                      bgcolor: "background.paper",
                                                      borderRadius: 0.5,
                                                      boxShadow: 1,
                                                      "&:hover": { bgcolor: "background.paper" },
                                                  }}
                                              >
                                                  <OpenInFullIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                      {imgUrl ? (
                                          <Box
                                              component="img"
                                              src={imgUrl}
                                              alt=""
                                              sx={{
                                                  position: "absolute",
                                                  inset: 0,
                                                  width: "100%",
                                                  height: "100%",
                                                  objectFit: "cover",
                                                  display: "block",
                                              }}
                                          />
                                      ) : (
                                          <Stack
                                              alignItems="center"
                                              justifyContent="center"
                                              spacing={0.5}
                                              sx={{
                                                  position: "absolute",
                                                  inset: 0,
                                                  color: "text.disabled",
                                                  px: 2,
                                              }}
                                          >
                                              <ImageNotSupportedOutlinedIcon sx={{ fontSize: 40 }} />
                                              <Typography variant="body2">No preview</Typography>
                                          </Stack>
                                      )}
                                  </Box>
                                  <CardContent sx={{ flexGrow: 1, pt: 1.5 }}>
                                      <Box
                                          sx={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                              gap: 0.25,
                                              minWidth: 0,
                                          }}
                                      >
                                          <Typography
                                              variant="subtitle1"
                                              component="div"
                                              sx={{
                                                  flex: 1,
                                                  minWidth: 0,
                                                  fontWeight: 600,
                                                  lineHeight: 1.35,
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: 3,
                                                  WebkitBoxOrient: "vertical",
                                                  overflow: "hidden",
                                              }}
                                          >
                                              {title}
                                          </Typography>
                                          <Tooltip title="Story">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label="Show story"
                                                      disabled={!sid}
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (!sid) return;
                                                          openStoryText(story);
                                                      }}
                                                      sx={{
                                                          flexShrink: 0,
                                                          width: 44,
                                                          height: 44,
                                                          mt: -0.75,
                                                      }}
                                                  >
                                                      <AutoStoriesOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                          <Tooltip title="Regenerate image">
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label="Regenerate image"
                                                      disabled={!sid || Boolean(regenImageLoadingId)}
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (!sid || regenImageLoadingId) return;
                                                          setRegenDialogStory({ id: sid, title });
                                                      }}
                                                      sx={{
                                                          flexShrink: 0,
                                                          width: 44,
                                                          height: 44,
                                                          mt: -0.75,
                                                          mr: -1,
                                                      }}
                                                  >
                                                      {regenImageLoadingId === sid ? (
                                                          <CircularProgress size={18} />
                                                      ) : (
                                                          <AutorenewIcon fontSize="small" />
                                                      )}
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      </Box>
                                      <Box
                                          sx={{
                                              mt: 0.75,
                                              display: "flex",
                                              alignItems: "flex-start",
                                              gap: 0.5,
                                          }}
                                      >
                                          <Box
                                              sx={{
                                                  flex: 1,
                                                  minWidth: 0,
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 0.25,
                                              }}
                                          >
                                              {tplPublishedLabel ? (
                                                  <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      component="div"
                                                  >
                                                      Published: {tplPublishedLabel}
                                                  </Typography>
                                              ) : null}
                                              <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  component="div"
                                              >
                                                  Facebook: {facebookPublishedLabel ?? "Not posted"}
                                              </Typography>
                                              <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  component="div"
                                              >
                                                  Instagram: {instagramPublishedLabel ?? "Not posted"}
                                              </Typography>
                                          </Box>
                                          {socialStillMissing ? (
                                              <Tooltip title="Posting this story for both social channels">
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          color="primary"
                                                          aria-label="Posting this story for both social channels"
                                                          disabled={!sid || publishingSocial}
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              if (!sid || publishingSocial) return;
                                                              setPublishSocialDialogStory({
                                                                  id: sid,
                                                                  title,
                                                                  facebookPosted: Boolean(facebookPublishedAt),
                                                                  instagramPosted: Boolean(instagramPublishedAt),
                                                              });
                                                          }}
                                                          sx={{
                                                              flexShrink: 0,
                                                              width: 44,
                                                              height: 44,
                                                              mt: -0.5,
                                                          }}
                                                      >
                                                          {publishingSocial ? (
                                                              <CircularProgress size={18} />
                                                          ) : (
                                                              <PublishIcon fontSize="small" />
                                                          )}
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          ) : null}
                                      </Box>
                                  </CardContent>
                              </Card>
                          );
                      })}
            </Box>

            {!loading && items.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                    No stories yet.
                </Typography>
            ) : null}

            {totalElements > 0 ? (
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{
                        mt: 3,
                        pt: 3,
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="story-page-size-bottom-label">Page size</InputLabel>
                        <Select
                            labelId="story-page-size-bottom-label"
                            label="Page size"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Pagination
                        color="primary"
                        count={pageCount}
                        page={page}
                        onChange={(_, value) => {
                            setPage(value);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        showFirstButton
                        showLastButton
                        disabled={loading}
                    />
                </Stack>
            ) : null}

            <Dialog
                fullScreen
                open={Boolean(detailStoryId)}
                onClose={() => closeStoryDetail()}
                PaperProps={{
                    sx: { bgcolor: "background.default", overflow: "auto" },
                }}
            >
                {detailStoryId ? (
                    <AdminStoryDetail
                        storyId={detailStoryId}
                        onClose={closeStoryDetail}
                    />
                ) : null}
            </Dialog>

            <Dialog
                open={Boolean(storyTextDialog)}
                onClose={() => setStoryTextDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{storyTextDialog?.title || "Story"}</DialogTitle>
                <DialogContent>
                    {storyTextDialog?.loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : storyTextDialog?.error ? (
                        <Typography color="error">{storyTextDialog.error}</Typography>
                    ) : (
                        <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                            {storyTextDialog?.text || "No story text."}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setStoryTextDialog(null)} sx={{ minHeight: 44, textTransform: "none" }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(batchDialog)} onClose={closeBatchDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {batchDialog === "story"
                        ? "Batch Story (gen-story)"
                        : batchDialog === "disable"
                            ? "Batch disabled content"
                            : "Batch Preview (gen-image)"}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {batchDialog === "story"
                            ? "POST /v1/story/gen-story — generate missing story text and main illustration for eligible published templates with a topic. The server waits about 2 seconds between each item."
                            : batchDialog === "disable"
                                ? "POST /v1/story/batch-disable-content — scan every DISABLED, DISCARDED, or deleted jigsaw and sliding puzzle, then disable related crossword, grid, sliding, and jigsaw for those stories."
                                : "POST /v1/story/gen-image — build image_preview_url (1200×630) for stories that already have image_url but no preview. The server waits about 2 seconds between each item."}
                    </Typography>
                    <TextField
                        margin="dense"
                        label="Batch size"
                        type="number"
                        fullWidth
                        value={batchSizeInput}
                        onChange={(e) => setBatchSizeInput(e.target.value)}
                        disabled={batchSubmitting}
                        slotProps={{
                            htmlInput: { min: 1, max: batchDialog === "disable" ? 5000 : 100 },
                        }}
                        helperText={
                            batchDialog === "disable"
                                ? "Allowed range 1–5000 (defaults to 5000). Processes all matching stories up to this cap."
                                : "Allowed range 1–100 (defaults to 10 if invalid)."
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeBatchDialog} disabled={batchSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => runBatch()}
                        disabled={batchSubmitting}
                        startIcon={
                            batchSubmitting ? (
                                <CircularProgress color="inherit" size={18} sx={{ mr: -0.5 }} />
                            ) : undefined
                        }
                    >
                        {batchSubmitting ? "Running…" : "Run batch"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(regenDialogStory)}
                setOpen={(open) => {
                    if (!open) setRegenDialogStory(null);
                }}
                title="Regenerate image?"
                message="This replaces the current illustration. Story text is not changed."
                targetName={
                    regenDialogStory?.title
                        ? `'${regenDialogStory.title}'`
                        : regenDialogStory?.id
                          ? `Story ${regenDialogStory.id}`
                          : "This story"
                }
                targetId={regenDialogStory?.id}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />

            <ConfirmDialog
                open={Boolean(publishSocialDialogStory)}
                setOpen={(open) => {
                    if (!open) setPublishSocialDialogStory(null);
                }}
                title="Posting to social channels?"
                message={
                    publishSocialDialogStory?.instagramPosted && !publishSocialDialogStory?.facebookPosted
                        ? "Instagram already has this image. Posting it to Facebook now."
                        : publishSocialDialogStory?.facebookPosted && !publishSocialDialogStory?.instagramPosted
                          ? "Facebook already has this image. Posting it to Instagram now."
                          : "Posting this story image to Facebook and Instagram now."
                }
                targetName={
                    publishSocialDialogStory?.title
                        ? `'${publishSocialDialogStory.title}'`
                        : publishSocialDialogStory?.id
                          ? `Story ${publishSocialDialogStory.id}`
                          : "This story"
                }
                targetId={publishSocialDialogStory?.id}
                callback={handleConfirmPublishSocial}
                warningAlert={
                    publishSocialDialogStory?.instagramPosted && !publishSocialDialogStory?.facebookPosted
                        ? "This posts the image publicly on the Facebook Page."
                        : publishSocialDialogStory?.facebookPosted && !publishSocialDialogStory?.instagramPosted
                          ? "This posts the image publicly on Instagram."
                          : "This posts publicly to both social channels."
                }
                okText="Posting now"
                cancelText="Cancel"
            />

            <StoryImageZoomDialog
                open={Boolean(fullImageStory?.url)}
                imageUrl={fullImageStory?.url || ""}
                title={fullImageStory?.title}
                onClose={() => setFullImageStory(null)}
                onDelete={
                    fullImageStory?.id
                        ? () => setDeleteDialogStory({
                            id: fullImageStory.id,
                            title: fullImageStory.title,
                        })
                        : undefined
                }
                deleting={Boolean(fullImageStory?.id && deletingStoryId === fullImageStory.id)}
            />

            <ConfirmDialog
                open={Boolean(deleteDialogStory)}
                setOpen={(open) => {
                    if (!open) setDeleteDialogStory(null);
                }}
                title="Delete story?"
                message={[
                    "This permanently deletes the story record, the story image, all sliding puzzles, and all jigsaw puzzles.",
                    "Facebook and Instagram posts of this image are removed when post ids are known.",
                    "The crossword or word-search puzzle is not deleted. Scheduled jobs will not recreate this story or its sliding/jigsaw puzzles.",
                ]}
                targetName={
                    deleteDialogStory?.title
                        ? `'${deleteDialogStory.title}'`
                        : deleteDialogStory?.id
                          ? `Story ${deleteDialogStory.id}`
                          : "This story"
                }
                targetId={deleteDialogStory?.id}
                callback={handleConfirmDeleteStory}
                warningAlert="This action cannot be undone."
                okText="OK"
                cancelText="Cancel"
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
